import { Skeleton } from '../../ui/Skeleton/Skeleton'
import { StatusBadge } from '../../ui/StatusBadge/StatusBadge'
import type { OnboardingApiClient } from '../onboarding/onboarding-api'
import type {
  ConfirmedOrganizationClinic,
  ManagerAccess,
  ManagerInvitation,
} from '../onboarding/organization-onboarding-contracts'
import { useCommunicationsStatus } from '../onboarding/use-communications-status'
import type { ReadSideRepository } from '../read-side/read-side-status'
import { useReadSideStatus } from '../read-side/use-read-side-status'
import {
  phoneStatus,
  workspaceStatus,
  type StatusView,
} from './organization-model'
import { PROVIDER_CHECK_STALE_MS } from './organization-queries'
import panel from './SettingsSection.module.css'
import styles from './OrganizationSettings.module.css'

/** Everything the page knows about manager access, for every clinic. */
export type ManagerAccessView = {
  /** A current, successful answer for this organization. */
  verified: boolean
  pending: boolean
  failed: boolean
  managers: ManagerAccess[]
  invitations: ManagerInvitation[]
  retry: () => void
  refresh: () => Promise<void>
}

/** One clinic's live status, shared by its row and its Manage dialog. */
export function useClinicStatus(
  clinic: ConfirmedOrganizationClinic,
  api: OnboardingApiClient,
  readSide: ReadSideRepository | undefined,
  enabled: boolean
) {
  const communications = useCommunicationsStatus({
    tenantUid: clinic.tenantUid,
    fallbackStatus: clinic.communicationsStatus,
    apiClient: api,
    providerVerification: true,
    enabled,
    staleTime: PROVIDER_CHECK_STALE_MS,
  })
  const readSideResult = useReadSideStatus(
    enabled ? readSide : undefined,
    clinic.tenantUid
  )
  const phone = phoneStatus({
    state: communications.state,
    verified: communications.verified,
    fallbackStatus: clinic.communicationsStatus,
    verificationPending: communications.verification.isFetching,
    verificationFailed: communications.verification.isError,
    readFailed:
      communications.query.isError ||
      (communications.query.data !== undefined && !communications.state),
    readPending: communications.query.isFetching,
  })
  const workspace = workspaceStatus(
    readSideResult.data?.tenantUid === clinic.tenantUid
      ? readSideResult.data
      : undefined,
    { dashboardReady: clinic.dashboardReady, isError: readSideResult.isError }
  )
  return { phone, workspace }
}

/** A status badge, or a quiet placeholder while its first answer loads. */
export function Status({ view }: { view: StatusView }) {
  if (view.pending)
    return (
      <span className={styles.checking} role="status">
        <span className={panel.srOnly}>Checking</span>
        <Skeleton width="4.75rem" height="1.375rem" />
      </span>
    )
  return <StatusBadge tone={view.tone}>{view.label}</StatusBadge>
}
